import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import styles from './styles/ChoicesList.module.css';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';

/**
 * The choices leaving a situation, in the order a player will see them.
 *
 * That order is not a stored field — it is the order of the outgoing edges,
 * which publish copies into `Situation.choices`. So a drag becomes one
 * `reorderEdges` op and nothing else.
 *
 * @param {{nodeId: string}} props
 */
const ChoicesList = ({ nodeId }) => {
    const { getOutgoingEdges, reorderChoices } = useCanvas();
    const { selectItem, setActiveEditTab } = useEditorUI();

    const choices = getOutgoingEdges(nodeId);

    // The list is rendered from local state during a drag so the item follows the
    // cursor without waiting for the op to round-trip; it re-syncs whenever the
    // graph changes underneath.
    const [displayChoices, setDisplayChoices] = useState(choices);

    useEffect(() => {
        setDisplayChoices(choices);
        // Comparing by id keeps a re-render from resetting an in-progress drag,
        // so the dep is the id list rather than the array identity.
    }, [nodeId, choices.map((edge) => edge.id).join(',')]);

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const reordered = Array.from(displayChoices);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        setDisplayChoices(reordered);
        reorderChoices(nodeId, reordered.map((edge) => edge.id));
    };

    const handleChoiceClick = (edgeId) => {
        selectItem('choice', edgeId);
        setActiveEditTab('1');
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable-choices">
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={styles.choicesContainer}
                    >
                        {displayChoices.length > 0 ? (
                            displayChoices.map((edge, index) => (
                                <Draggable key={edge.id} draggableId={edge.id} index={index}>
                                    {(dragProvided, snapshot) => (
                                        <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                            className={`${styles.choiceItem} ${
                                                snapshot.isDragging ? styles.dragging : ''
                                            }`}
                                            onClick={() =>
                                                !snapshot.isDragging && handleChoiceClick(edge.id)
                                            }
                                            style={{ ...dragProvided.draggableProps.style }}
                                            aria-label={`Choice: ${
                                                edge.data?.title || 'Untitled'
                                            }. Position ${index + 1} of ${displayChoices.length}.`}
                                        >
                                            <div className={styles.choiceTitle}>
                                                {edge.data?.title || 'Untitled'}
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))
                        ) : (
                            <p className={styles.noChoices}>No choices defined for this situation.</p>
                        )}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default ChoicesList;
